import React, { useState, useRef } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import { useSafeLocation } from '../../../../../hooks/useSafeLocation';
import { ScreenshotButton } from './ScreenshotButton';
import { PDFButton } from './PDFButton';
import { PrintWrapper } from './PrintWrapper';
import { useChatStore, type ChatState } from '../../../store/chatStore';
import { PleriClient } from '../../../client';
import { type PrintMode } from '../../../../../utils/pdfUtils';

import { useTranslation } from 'react-i18next';

interface ExpandableContainerProps {
  children: (isExpanded: boolean) => React.ReactNode;
  title: string;
  className?: string;
  minimizedHeight?: string;
  toolbar?: React.ReactNode;
  enableScreenshot?: boolean;
  screenshotFilename?: string;
  enablePDF?: boolean;
  pdfFilename?: string;
  defaultExpanded?: boolean;
  printMode?: PrintMode;
  enableFocus?: boolean;
  enableExpand?: boolean;
}

export const ExpandableContainer: React.FC<ExpandableContainerProps> = ({
  children,
  title,
  className = '',
  minimizedHeight = '350px',
  toolbar,
  enableScreenshot = false,
  screenshotFilename,
  enablePDF = false,
  pdfFilename,
  defaultExpanded = false,
  printMode = 'image',
  enableFocus = true,
  enableExpand = true,
}) => {
  const { t } = useTranslation('pleri/chat');
  const contentRef = useRef<HTMLDivElement>(null);
  const location = useSafeLocation();

  // Check if we're on the mini route
  const isMiniRoute = location.pathname === '/pleri/mini';
  const [isExpanded, setIsExpanded] = useState(defaultExpanded && !isMiniRoute);

  // Check if focused view is open
  const isFocusedViewOpen = useChatStore((state: ChatState) => {
    const activeConversation = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    return activeConversation?.focusedViewOpen || false;
  });

  const baseStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    transition:
      'width 0.3s ease, height 0.3s ease, top 0.3s ease, left 0.3s ease',
  };

  const normalStyle: React.CSSProperties = {
    ...baseStyle,
    position: 'relative',
    height: isFocusedViewOpen ? '150px' : minimizedHeight,
    padding: '0',
  };

  const expandedStyle: React.CSSProperties = {
    ...baseStyle,
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    height: '90vh',
    zIndex: 1000,
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    padding: '0',
  };

  const handleToggleExpand = () => {
    if (isMiniRoute) {
      return; // Don't allow expansion in mini view
    }
    setIsExpanded(!isExpanded);
  };

  const handleFocus = () => {
    setIsExpanded(false);
    PleriClient.updateChatInput({
      prompt: '',
      useExistingConversation: true,
      appendContext: true,
      focusedView: {
        content: children(true),
        title: title,
        printMode,
      },
    });
  };

  return (
    <div
      style={isExpanded ? expandedStyle : normalStyle}
      className={`${
        isExpanded
          ? 'overflow-hidden rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
          : 'relative w-auto rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
      } ${className} overflow-hidden shadow-md`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-300 bg-white p-2 px-3 dark:border-gray-600 dark:bg-gray-900">
        <div className="flex items-center">
          <div className="font-sans text-sm font-medium text-gray-700 dark:text-gray-300">
            {title}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {toolbar && <div className="flex items-center">{toolbar}</div>}
          {enableFocus && (
            <button
              onClick={handleFocus}
              className="flex items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-1.5 py-1 text-orange-600 hover:bg-orange-100 hover:text-orange-700 focus:ring-2 focus:ring-orange-300 focus:outline-none dark:border-gray-600 dark:bg-gray-800"
              title={t('focusOnThisContent')}
            >
              <ArrowsPointingOutIcon className="h-4 w-4" />
              <span className="text-sm font-medium">{t('focus')}</span>
            </button>
          )}
          {enableScreenshot && (
            <ScreenshotButton
              targetRef={contentRef}
              filename={
                screenshotFilename ||
                `${title.toLowerCase().replace(/\s+/g, '-')}-screenshot.png`
              }
              title={t('takeScreenshotOfContent')}
            />
          )}
          {enablePDF && (
            <PDFButton
              targetRef={contentRef}
              filename={
                pdfFilename || `${title.toLowerCase().replace(/\s+/g, '-')}.pdf`
              }
              title={t('downloadAsPdf')}
              printMode={printMode}
            />
          )}
          {enableExpand && (
            <button
              onClick={handleToggleExpand}
              disabled={isMiniRoute}
              className={`rounded p-1 focus:ring-1 focus:ring-gray-300 focus:outline-none dark:focus:ring-gray-600 ${
                isMiniRoute
                  ? 'cursor-not-allowed text-gray-300'
                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-400'
              }`}
              title={
                isMiniRoute
                  ? t('expandDisabledInMiniView')
                  : isExpanded
                    ? t('minimize')
                    : t('expand')
              }
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-3.5 w-3.5" />
              ) : (
                <ChevronUpIcon className="h-3.5 w-3.5" />
              )}
            </button>
          )}
        </div>
      </div>
      {/* Content */}
      <div
        ref={contentRef}
        className="flex-1 overflow-auto"
        style={{
          height: isExpanded
            ? 'calc(100vh - 40px)'
            : `calc(${minimizedHeight} - 40px)`,
        }}
      >
        {printMode === 'text' ? (
          <PrintWrapper>{children(isExpanded)}</PrintWrapper>
        ) : (
          children(isExpanded)
        )}
      </div>
    </div>
  );
};
